import type { RefObject } from "react";
import { useCallback, useEffect, useState } from "react";
import { useLatestRef } from "@design-system/hooks/useLatestRef";
import type { DensityChartWindow } from "./DensityChart.lib";
import { clampToHistory } from "./DensityChart.lib";
import type { DensityChartBucketUnit } from "./densityBuckets.lib";
import { fitDensity } from "./densityFit.lib";
import type { HeldBars, PanDirection } from "./densityViewport.lib";
import {
    BAR_PITCH_PX,
    canPanToward,
    offsetForWindow,
    panOffset,
    panTo,
    swapOffset,
    TODAY_INSET_PX,
    toDensityViewport,
    withHeldBars,
} from "./densityViewport.lib";
import { useElementWidth } from "./useElementWidth";

interface UseDensityViewportParams {
    viewportRef: RefObject<HTMLDivElement | null>;
    /** The chart, holding the viewport and the slice card, where a sideways wheel pans. */
    root: HTMLElement | null;
    startDate: string;
    endDate: string;
    /** The closest unit wanted; the bars show a coarser one while the window doesn't fit it. */
    closestUnit: DensityChartBucketUnit;
    window?: DensityChartWindow;
    /** Called when the bars move, e.g. to close a card pinned to one. */
    onPan: () => void;
}

interface Placement {
    unit: DensityChartBucketUnit;
    /** The unit, history start and whether the width is known, as last placed. */
    placeKey: string;
    windowKey: string;
    offset: number;
    isEasing: boolean;
}

const toWindowKey = (window: DensityChartWindow | undefined) =>
    window ? `${window.start}/${window.end}` : "";

const LINE_PX = 16;
const DOM_DELTA_LINE = 1;
const DOM_DELTA_PAGE = 2;

/** A wheel's sideways delta in px: a line is about 16px and a page `pagePx`. */
const wheelDeltaPx = (
    { deltaX, deltaMode }: Pick<WheelEvent, "deltaX" | "deltaMode">,
    pagePx: number,
) => {
    if (deltaMode === DOM_DELTA_LINE) {
        return deltaX * LINE_PX;
    }
    return deltaMode === DOM_DELTA_PAGE ? deltaX * pagePx : deltaX;
};

const toPlaceKey = ({
    unit,
    startDate,
    hasWidth,
}: {
    unit: DensityChartBucketUnit;
    startDate: string;
    hasWidth: boolean;
}) => [unit, startDate, hasWidth].join("|");

/**
 * Where the bars sit, in the viewport less `TODAY_INSET_PX` on the right,
 * where today's bar stops, and in which unit: the closest one the window
 * fits (`fitDensity`), worked out again for every window and width. It
 * shows the window when the chart mounts or gets a window it didn't pick
 * itself (a preset, say) that isn't already in full view, easing only in
 * the last case. A unit change keeps the midpoint of the window it showed
 * or picked in place (`swapOffset`); a new window from outside is shown
 * instead. Arrows pan most of a screen, eased, and say whether
 * there's more that way; a sideways wheel or trackpad over the chart pans
 * with the gesture, except while a drag holds the bars.
 */
export function useDensityViewport({
    viewportRef,
    root,
    startDate,
    endDate,
    closestUnit,
    window,
    onPan,
}: UseDensityViewportParams) {
    const viewportWidth = useElementWidth(viewportRef);
    const width = Math.max(viewportWidth - TODAY_INSET_PX, 0);
    const shownWindow = window && clampToHistory(window, startDate);
    const unit =
        shownWindow && width > 0
            ? fitDensity({
                  window: {
                      ...shownWindow,
                      end:
                          shownWindow.end > endDate ? endDate : shownWindow.end,
                  },
                  unit: closestUnit,
                  visibleBars: width / BAR_PITCH_PX,
              })
            : closestUnit;
    const placeKey = toPlaceKey({ unit, startDate, hasWidth: width > 0 });
    const windowKey = toWindowKey(window);
    const [placement, setPlacement] = useState<Placement>({
        unit,
        placeKey: "",
        windowKey: "",
        offset: 0,
        isEasing: false,
    });
    const [pickedWindowKey, setPickedWindowKey] = useState<string | null>(null);

    if (placement.placeKey !== placeKey || placement.windowKey !== windowKey) {
        const isSamePlace = placement.placeKey === placeKey;
        const shouldPlace = !isSamePlace || windowKey !== pickedWindowKey;
        if (windowKey === pickedWindowKey) {
            setPickedWindowKey(null);
        }
        const isSwap =
            placement.unit !== unit &&
            placement.placeKey ===
                toPlaceKey({ unit: placement.unit, startDate, hasWidth: true });
        const isSameOrPickedWindow =
            windowKey === placement.windowKey || windowKey === pickedWindowKey;
        const timeline = { startDate, endDate, width };
        let { offset } = placement;
        if (window && isSwap && isSameOrPickedWindow) {
            offset = swapOffset({
                ...timeline,
                window,
                from: {
                    unit: placement.unit,
                    offset: toDensityViewport({
                        ...timeline,
                        unit: placement.unit,
                        offset,
                    }).offset,
                },
                to: unit,
            });
        } else if (window && shouldPlace) {
            offset = offsetForWindow({
                ...timeline,
                unit,
                window,
                offset: isSamePlace ? offset : undefined,
            });
        }
        setPlacement({
            unit,
            placeKey,
            windowKey,
            offset,
            isEasing: isSamePlace && shouldPlace,
        });
    }

    const [heldBars, setHeldBars] = useState<HeldBars | null>(null);
    const viewport = withHeldBars(
        toDensityViewport({
            startDate,
            endDate,
            unit,
            width,
            offset: placement.offset,
        }),
        heldBars?.unit === unit ? heldBars : null,
    );

    const latestRef = useLatestRef({
        unit,
        viewport,
        width,
        isHolding: heldBars !== null,
        onPan,
    });

    const moveTo = useCallback(
        ({ offset, isEasing }: { offset: number; isEasing: boolean }) => {
            latestRef.current.onPan();
            setPlacement((current) => ({ ...current, offset, isEasing }));
        },
        [latestRef],
    );

    const pan = useCallback(
        (direction: PanDirection) => {
            const { viewport: current } = latestRef.current;
            const offset = panOffset({ viewport: current, direction });
            moveTo({ offset, isEasing: true });
            return canPanToward({
                offset,
                maxOffset: current.maxOffset,
                direction,
            });
        },
        [latestRef, moveTo],
    );

    /** Pans by px like `panTo`, negative for earlier. */
    const panByPx = useCallback(
        (px: number) => {
            const { viewport: current, onPan: notifyPan } = latestRef.current;
            const { maxOffset } = current;
            if (
                px === 0 ||
                (px > 0 ? current.offset <= 0 : current.offset >= maxOffset)
            ) {
                return;
            }
            notifyPan();
            setPlacement((previous) => {
                const from = Math.min(previous.offset, maxOffset);
                return {
                    ...previous,
                    offset: panTo({
                        viewport: { ...current, offset: from },
                        offset: from - px / BAR_PITCH_PX,
                    }),
                    isEasing: false,
                };
            });
        },
        [latestRef],
    );

    useEffect(() => {
        if (!root) {
            return;
        }
        const handleWheel = (event: WheelEvent) => {
            if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) {
                return;
            }
            event.preventDefault();
            const { isHolding, width: pagePx } = latestRef.current;
            if (!isHolding) {
                panByPx(wheelDeltaPx(event, pagePx));
            }
        };
        root.addEventListener("wheel", handleWheel, { passive: false });
        return () => root.removeEventListener("wheel", handleWheel);
    }, [root, latestRef, panByPx]);

    /** Keeps drawing the bars drawn now until `releaseBars` or a unit change, e.g. through a drag. */
    const holdBars = useCallback(() => {
        const {
            unit: heldUnit,
            viewport: { firstBar, barCount },
        } = latestRef.current;
        setHeldBars({ unit: heldUnit, firstBar, barEnd: firstBar + barCount });
    }, [latestRef]);
    const releaseBars = useCallback(() => setHeldBars(null), []);

    /** Marks a window the chart picked, so the bars stay where they are. */
    const markPickedWindow = useCallback(
        (picked: DensityChartWindow) => setPickedWindowKey(toWindowKey(picked)),
        [],
    );

    return {
        unit,
        viewport,
        width,
        isEasing: placement.isEasing,
        pan,
        panByPx,
        holdBars,
        releaseBars,
        markPickedWindow,
    };
}
